import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Envelope, Users, ListBullets, PaperPlaneTilt, ChartBar, Plus, ArrowsClockwise, MagnifyingGlass, Trash, UserPlus, Export, Warning, Copy, Eye } from '@phosphor-icons/react';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AcumbamailPanel({ lang, onClose }) {
  const [tab, setTab] = useState('lists');
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateList, setShowCreateList] = useState(false);
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncListId, setSyncListId] = useState(null);
  const [deleteListId, setDeleteListId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [sendCampaignId, setSendCampaignId] = useState(null);
  const [sendCampaignName, setSendCampaignName] = useState('');
  const [sending, setSending] = useState(false);
  const [duplicateDefaults, setDuplicateDefaults] = useState(null);
  const [reportCampaignId, setReportCampaignId] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const lb = lang === 'es' ? {
    lists: 'Listas', templates: 'Plantillas', campaigns: 'Campanas', stats: 'Estadisticas',
    name: 'Nombre', email: 'Email', status: 'Estado', actions: 'Acciones',
    subscribers: 'Suscriptores', refresh: 'Actualizar', create: 'Crear',
    createList: 'Crear Lista', listName: 'Nombre de la lista', senderEmail: 'Email remitente',
    company: 'Empresa', city: 'Ciudad', address: 'Direccion', phone: 'Telefono',
    cancel: 'Cancelar', save: 'Guardar', send: 'Enviar', loading: 'Cargando...',
    noLists: 'No hay listas', noTemplates: 'No hay plantillas', noCampaigns: 'No hay campanas',
    noSubscribers: 'No hay suscriptores en esta lista', clickList: 'Selecciona una lista para ver suscriptores',
    syncVolunteers: 'Sincronizar Voluntarios', syncDesc: 'Sincronizar voluntarios de la base de datos con esta lista de Acumbamail',
    filter: 'Filtro', allVolunteers: 'Todos los voluntarios', activeOnly: 'Solo activos',
    syncing: 'Sincronizando...', synced: 'sincronizados', skipped: 'omitidos (sin email)',
    createCampaign: 'Crear Campana', subject: 'Asunto', fromName: 'Nombre remitente',
    fromEmail: 'Email remitente', selectList: 'Seleccionar lista', selectTemplate: 'Seleccionar plantilla',
    confirmSend: 'Enviar campana', sendWarning: 'Se enviara a todos los suscriptores de la lista. Esta accion no se puede deshacer.',
    total: 'Total', active: 'Activos', available: 'Disponibles', unavailable: 'No disponible en Acumbamail', unavailableHint: 'Acumbamail la marca como no disponible: si el envío falla, revísala allí (que esté guardada o finalizada)', deleteList: 'Eliminar lista',
    addSubscriber: 'Anadir suscriptor', search: 'Buscar...', body: 'Contenido HTML',
    createTemplate: 'Crear Plantilla', templateName: 'Nombre de la plantilla',
    templateBody: 'Contenido HTML de la plantilla', confirmDeleteList: 'Eliminar esta lista?',
    confirmDeleteListDesc: 'Se eliminara la lista y todos sus suscriptores de Acumbamail. Esta accion no se puede deshacer.',
    noDeleteTemplates: 'Las plantillas solo se pueden eliminar desde la web de Acumbamail',
    sendCampaign: 'Enviar', confirmSendTitle: 'Enviar campana?',
    confirmSendDesc: 'Se enviara el email a TODOS los suscriptores de la lista asociada. Esta accion no se puede deshacer.',
    campaignSent: 'Campana enviada correctamente',
    duplicate: 'Duplicar',
    report: 'Informe', reportTitle: 'Informe de Campana',
    sent: 'Enviados', delivered: 'Entregados', opened: 'Abiertos', unopened: 'No abiertos',
    clicks: 'Clics', uniqueClicks: 'Clics unicos', softBounces: 'Rebotes suaves', hardBounces: 'Rebotes duros',
    unsubscribes: 'Bajas', complaints: 'Quejas', openRate: 'Tasa apertura', clickRate: 'Tasa clic',
    bounceRate: 'Tasa rebote', openersList: 'Quien abrio el email', openDate: 'Fecha apertura',
    ispBreakdown: 'Desglose por proveedor', noReport: 'No hay datos de informe disponibles',
    campaignUrl: 'Ver campana online', close: 'Cerrar',
  } : {
    lists: 'Lists', templates: 'Templates', campaigns: 'Campaigns', stats: 'Statistics',
    name: 'Name', email: 'Email', status: 'Status', actions: 'Actions',
    subscribers: 'Subscribers', refresh: 'Refresh', create: 'Create',
    createList: 'Create List', listName: 'List name', senderEmail: 'Sender email',
    company: 'Company', city: 'City', address: 'Address', phone: 'Phone',
    cancel: 'Cancel', save: 'Save', send: 'Send', loading: 'Loading...',
    noLists: 'No lists', noTemplates: 'No templates', noCampaigns: 'No campaigns',
    noSubscribers: 'No subscribers in this list', clickList: 'Select a list to view subscribers',
    syncVolunteers: 'Sync Volunteers', syncDesc: 'Sync volunteers from database to this Acumbamail list',
    filter: 'Filter', allVolunteers: 'All volunteers', activeOnly: 'Active only',
    syncing: 'Syncing...', synced: 'synced', skipped: 'skipped (no email)',
    createCampaign: 'Create Campaign', subject: 'Subject', fromName: 'Sender name',
    fromEmail: 'Sender email', selectList: 'Select list', selectTemplate: 'Select template',
    confirmSend: 'Send campaign', sendWarning: 'This will send to all subscribers in the list. This action cannot be undone.',
    total: 'Total', active: 'Active', available: 'Available', unavailable: 'Not available in Acumbamail', unavailableHint: 'Acumbamail marks it as unavailable: if sending fails, check it there (saved or finished)', deleteList: 'Delete list',
    addSubscriber: 'Add subscriber', search: 'Search...', body: 'HTML content',
    createTemplate: 'Create Template', templateName: 'Template name',
    templateBody: 'Template HTML content', confirmDeleteList: 'Delete this list?',
    confirmDeleteListDesc: 'The list and all its subscribers will be deleted from Acumbamail. This action cannot be undone.',
    noDeleteTemplates: 'Templates can only be deleted from the Acumbamail website',
    sendCampaign: 'Send', confirmSendTitle: 'Send campaign?',
    confirmSendDesc: 'This will send the email to ALL subscribers in the associated list. This action cannot be undone.',
    campaignSent: 'Campaign sent successfully',
    duplicate: 'Duplicate',
    report: 'Report', reportTitle: 'Campaign Report',
    sent: 'Sent', delivered: 'Delivered', opened: 'Opened', unopened: 'Unopened',
    clicks: 'Clicks', uniqueClicks: 'Unique clicks', softBounces: 'Soft bounces', hardBounces: 'Hard bounces',
    unsubscribes: 'Unsubscribes', complaints: 'Complaints', openRate: 'Open rate', clickRate: 'Click rate',
    bounceRate: 'Bounce rate', openersList: 'Who opened the email', openDate: 'Open date',
    ispBreakdown: 'ISP breakdown', noReport: 'No report data available',
    campaignUrl: 'View campaign online', close: 'Close',
  };

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/acumbamail/lists`, { withCredentials: true });
      setLists(r.data || []);
    } catch (err) {
      toast.error(errText(err, 'Error loading lists'));
    }
    setLoading(false);
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/acumbamail/templates`, { withCredentials: true });
      setTemplates(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(errText(err, 'Error loading templates'));
    }
    setLoading(false);
  }, []);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/acumbamail/campaigns`, { withCredentials: true });
      setCampaigns(r.data || []);
    } catch (err) {
      toast.error(errText(err, 'Error loading campaigns'));
    }
    setLoading(false);
  }, []);

  const loadSubscribers = useCallback(async (listId) => {
    setSubsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/acumbamail/lists/${listId}/subscribers`, { withCredentials: true });
      setSubscribers(r.data || []);
    } catch (err) {
      toast.error(errText(err));
      setSubscribers([]);
    }
    setSubsLoading(false);
  }, []);

  useEffect(() => { loadLists(); loadTemplates(); }, [loadLists, loadTemplates]);

  useEffect(() => {
    if (tab === 'templates') loadTemplates();
    if (tab === 'campaigns') loadCampaigns();
  }, [tab, loadTemplates, loadCampaigns]);

  const handleSelectList = (list) => {
    setSelectedList(list);
    loadSubscribers(list.id);
  };

  const handleDeleteList = async () => {
    if (!deleteListId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/acumbamail/lists/${deleteListId}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Lista eliminada' : 'List deleted');
      if (selectedList?.id === deleteListId) { setSelectedList(null); setSubscribers([]); }
      loadLists();
    } catch (err) {
      toast.error(errText(err));
    }
    setDeleting(false);
    setDeleteListId(null);
  };

  const handleSendCampaign = async () => {
    if (!sendCampaignId) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/acumbamail/campaigns/${sendCampaignId}/send`, {}, { withCredentials: true });
      toast.success(lb.campaignSent);
      loadCampaigns();
    } catch (err) {
      toast.error(errText(err));
    }
    setSending(false);
    setSendCampaignId(null);
    setSendCampaignName('');
  };

  const handleDuplicate = async (campaignId) => {
    try {
      const r = await axios.get(`${API_URL}/api/acumbamail/campaigns/${campaignId}/stats`, { withCredentials: true });
      const info = r.data;
      setDuplicateDefaults({
        name: (info.name || '') + ' - Copia',
        subject: info.subject || '',
        from_name: 'Laboratorio Goya Innova',
        from_email: info.email_from || '',
      });
      setShowCreateCampaign(true);
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const loadReport = async (campaignId) => {
    setReportCampaignId(campaignId);
    setReportLoading(true);
    setReportData(null);
    try {
      const r = await axios.get(`${API_URL}/api/acumbamail/campaigns/${campaignId}/report`, { withCredentials: true });
      setReportData(r.data);
    } catch (err) {
      toast.error(errText(err, 'Error cargando informe'));
    }
    setReportLoading(false);
  };

  const filteredSubscribers = subscribers.filter(s =>
    !searchTerm || s.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="acumbamail-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Envelope size={20} weight="duotone" className="text-emerald-600" />
          <span className="text-sm font-semibold text-stone-800 uppercase tracking-wider">Acumbamail</span>
        </div>
        {onClose && (
          <Button variant="ghost" size="sm" onClick={onClose} className="text-stone-400 hover:text-stone-600">
            &times;
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-stone-100 w-full justify-start">
          <TabsTrigger value="lists" data-testid="acumba-tab-lists" className="gap-1.5 text-xs">
            <ListBullets size={14} /> {lb.lists} <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">{lists.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="templates" data-testid="acumba-tab-templates" className="gap-1.5 text-xs">
            <Envelope size={14} /> {lb.templates} <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">{templates.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="acumba-tab-campaigns" className="gap-1.5 text-xs">
            <PaperPlaneTilt size={14} /> {lb.campaigns} <Badge variant="secondary" className="ml-1 text-[10px] px-1.5">{campaigns.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* ── LISTS TAB ── */}
        <TabsContent value="lists" className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={loadLists} disabled={loading} className="gap-1.5 text-xs" data-testid="acumba-refresh-lists">
              <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} /> {lb.refresh}
            </Button>
            <Button size="sm" onClick={() => setShowCreateList(true)} className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="acumba-create-list">
              <Plus size={12} /> {lb.createList}
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* List of lists */}
            <div className="space-y-2">
              {loading ? (
                <p className="text-xs text-stone-400 p-4">{lb.loading}</p>
              ) : lists.length === 0 ? (
                <p className="text-xs text-stone-400 p-4">{lb.noLists}</p>
              ) : (
                lists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => handleSelectList(list)}
                    data-testid={`acumba-list-${list.id}`}
                    className={`w-full text-left p-3 rounded border transition-all text-sm ${
                      selectedList?.id === list.id
                        ? 'border-emerald-400 bg-emerald-50 shadow-sm'
                        : 'border-stone-200 hover:border-stone-300 hover:bg-stone-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Users size={14} className="text-stone-400" />
                        <span className="font-medium text-stone-800">{list.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost" size="sm"
                          onClick={e => { e.stopPropagation(); setSyncListId(list.id); setShowSyncDialog(true); }}
                          className="h-7 w-7 p-0 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          title={lb.syncVolunteers}
                          data-testid={`acumba-sync-${list.id}`}
                        >
                          <Export size={14} />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={e => { e.stopPropagation(); setDeleteListId(list.id); }}
                          className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                          title={lb.deleteList}
                          data-testid={`acumba-delete-list-${list.id}`}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                    {list.description && <p className="text-[11px] text-stone-400 mt-1">{list.description}</p>}
                  </button>
                ))
              )}
            </div>

            {/* Subscribers panel */}
            <Card className="border-stone-200 rounded-sm shadow-none">
              <CardHeader className="pb-2 pt-3 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-semibold text-stone-700">
                    {selectedList ? `${lb.subscribers} — ${selectedList.name}` : lb.clickList}
                  </CardTitle>
                  {selectedList && (
                    <Badge variant="outline" className="text-[10px]">
                      {filteredSubscribers.length} {lb.total}
                    </Badge>
                  )}
                </div>
                {selectedList && (
                  <div className="relative mt-2">
                    <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
                    <Input
                      data-testid="acumba-subscriber-search"
                      placeholder={lb.search}
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="pl-8 h-8 text-xs"
                    />
                  </div>
                )}
              </CardHeader>
              <CardContent className="px-4 pb-3">
                {!selectedList ? (
                  <p className="text-xs text-stone-400 text-center py-6">{lb.clickList}</p>
                ) : subsLoading ? (
                  <p className="text-xs text-stone-400 text-center py-6">{lb.loading}</p>
                ) : filteredSubscribers.length === 0 ? (
                  <p className="text-xs text-stone-400 text-center py-6">{lb.noSubscribers}</p>
                ) : (
                  <div className="max-h-[400px] overflow-y-auto space-y-1">
                    {filteredSubscribers.map((sub, i) => (
                      <div key={sub.email || i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-stone-50 text-xs border-b border-stone-100 last:border-0">
                        <span className="text-stone-700 truncate flex-1">{sub.email}</span>
                        <Badge variant="outline" className={`text-[9px] ml-2 ${sub.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-stone-50 text-stone-500'}`}>
                          {sub.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── TEMPLATES TAB ── */}
        <TabsContent value="templates" className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={loadTemplates} disabled={loading} className="gap-1.5 text-xs" data-testid="acumba-refresh-templates">
              <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} /> {lb.refresh}
            </Button>
            <Button size="sm" onClick={() => setShowCreateTemplate(true)} className="gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="acumba-create-template">
              <Plus size={12} /> {lb.createTemplate}
            </Button>
          </div>
          <p className="text-[11px] text-stone-400 flex items-center gap-1 mb-3">
            <Warning size={12} /> {lb.noDeleteTemplates}
          </p>
          {loading ? (
            <p className="text-xs text-stone-400 p-4">{lb.loading}</p>
          ) : templates.length === 0 ? (
            <p className="text-xs text-stone-400 p-4">{lb.noTemplates}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {templates.map(tpl => (
                <div key={tpl.id} data-testid={`acumba-template-${tpl.id}`} title={tpl.available === false ? lb.unavailableHint : undefined}
                  className="p-3 border border-stone-200 rounded hover:border-emerald-300 hover:bg-emerald-50/30 transition-colors">
                  <div className="flex items-center gap-2">
                    <Envelope size={14} className="text-emerald-500 flex-shrink-0" />
                    <span className="text-xs font-medium text-stone-700 truncate">{tpl.name}</span>
                  </div>
                  <div className="flex items-center gap-1 mt-1.5">
                    <Badge variant="outline" className="text-[9px]">ID: {tpl.id}</Badge>
                    {tpl.available && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200">{lb.available}</Badge>}
                    {tpl.available === false && <Badge variant="outline" data-testid={`acumba-template-unavailable-${tpl.id}`} className="text-[9px] bg-stone-100 text-stone-500 border-stone-300">{lb.unavailable}</Badge>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── CAMPAIGNS TAB ── */}
        <TabsContent value="campaigns" className="mt-4">
          <div className="flex items-center gap-2 mb-3">
            <Button size="sm" variant="outline" onClick={loadCampaigns} disabled={loading} className="gap-1.5 text-xs" data-testid="acumba-refresh-campaigns">
              <ArrowsClockwise size={12} className={loading ? 'animate-spin' : ''} /> {lb.refresh}
            </Button>
            <Button size="sm" onClick={() => setShowCreateCampaign(true)} className="gap-1.5 text-xs bg-[#1f3e80] hover:bg-[#2a4f8a] text-white" data-testid="acumba-create-campaign">
              <Plus size={12} /> {lb.createCampaign}
            </Button>
          </div>
          {loading ? (
            <p className="text-xs text-stone-400 p-4">{lb.loading}</p>
          ) : campaigns.length === 0 ? (
            <p className="text-xs text-stone-400 p-4">{lb.noCampaigns}</p>
          ) : (
            <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
              {campaigns.map(c => (
                <div key={c.id} data-testid={`acumba-campaign-${c.id}`}
                  className="flex items-center justify-between p-2.5 border border-stone-200 rounded hover:bg-stone-50 transition-colors">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <PaperPlaneTilt size={14} className="text-[#1f3e80] flex-shrink-0" />
                    <span className="text-xs font-medium text-stone-700 truncate">{c.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[9px]">ID: {c.id}</Badge>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => loadReport(c.id)}
                      className="h-7 gap-1.5 text-[11px] border-emerald-300 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"
                      data-testid={`acumba-report-campaign-${c.id}`}
                    >
                      <ChartBar size={12} /> {lb.report}
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => handleDuplicate(c.id)}
                      className="h-7 gap-1.5 text-[11px] border-stone-300 text-stone-600 hover:text-stone-800"
                      data-testid={`acumba-duplicate-campaign-${c.id}`}
                    >
                      <Copy size={12} /> {lb.duplicate}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setSendCampaignId(c.id); setSendCampaignName(c.name); }}
                      className="h-7 gap-1.5 text-[11px] bg-[#1f3e80] hover:bg-[#2a4f8a] text-white"
                      data-testid={`acumba-send-campaign-${c.id}`}
                    >
                      <PaperPlaneTilt size={12} /> {lb.sendCampaign}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ── CREATE LIST DIALOG ── */}
      <CreateListDialog
        open={showCreateList}
        onClose={() => setShowCreateList(false)}
        onCreated={() => { setShowCreateList(false); loadLists(); }}
        lang={lang}
        lb={lb}
      />

      {/* ── CREATE CAMPAIGN DIALOG ── */}
      <CreateCampaignDialog
        open={showCreateCampaign}
        onClose={() => { setShowCreateCampaign(false); setDuplicateDefaults(null); }}
        onCreated={() => { setShowCreateCampaign(false); setDuplicateDefaults(null); loadCampaigns(); }}
        lists={lists}
        templates={templates}
        lang={lang}
        lb={lb}
        defaults={duplicateDefaults}
      />

      {/* ── SYNC VOLUNTEERS DIALOG ── */}
      <SyncVolunteersDialog
        open={showSyncDialog}
        onClose={() => setShowSyncDialog(false)}
        listId={syncListId}
        listName={lists.find(l => l.id === syncListId)?.name || ''}
        onSynced={() => { setShowSyncDialog(false); if (selectedList?.id === syncListId) loadSubscribers(syncListId); }}
        lang={lang}
        lb={lb}
      />

      {/* ── DELETE LIST CONFIRM ── */}
      <ConfirmDeleteDialog
        open={!!deleteListId}
        onOpenChange={() => setDeleteListId(null)}
        title={lb.confirmDeleteList}
        description={`${lb.confirmDeleteListDesc} (${lists.find(l => l.id === deleteListId)?.name || ''})`}
        onConfirm={handleDeleteList}
        loading={deleting}
      />

      {/* ── SEND CAMPAIGN CONFIRM ── */}
      <Dialog open={!!sendCampaignId} onOpenChange={() => { if (!sending) { setSendCampaignId(null); setSendCampaignName(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="send-campaign-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <PaperPlaneTilt size={18} className="text-[#1f3e80]" />
              {lb.confirmSendTitle}
            </DialogTitle>
            <DialogDescription className="text-xs text-stone-500">
              {lb.confirmSendDesc}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-800 flex items-start gap-2">
            <Warning size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              <strong>{lang === 'es' ? 'Campana' : 'Campaign'}:</strong> {sendCampaignName}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setSendCampaignId(null); setSendCampaignName(''); }} disabled={sending} className="text-xs">
              {lb.cancel}
            </Button>
            <Button onClick={handleSendCampaign} disabled={sending} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white text-xs gap-1.5" data-testid="confirm-send-campaign">
              <PaperPlaneTilt size={14} />
              {sending ? lb.loading : lb.sendCampaign}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── CREATE TEMPLATE DIALOG ── */}
      <CreateTemplateDialog
        open={showCreateTemplate}
        onClose={() => setShowCreateTemplate(false)}
        onCreated={() => { setShowCreateTemplate(false); loadTemplates(); }}
        lang={lang}
        lb={lb}
      />

      {/* ── CAMPAIGN REPORT DIALOG ── */}
      <CampaignReportDialog
        open={!!reportCampaignId}
        onClose={() => { setReportCampaignId(null); setReportData(null); }}
        data={reportData}
        loading={reportLoading}
        campaignName={campaigns.find(c => c.id === reportCampaignId)?.name || ''}
        lang={lang}
        lb={lb}
      />
    </div>
  );
}


function CreateListDialog({ open, onClose, onCreated, lang, lb }) {
  const [form, setForm] = useState({ name: '', sender_email: '', company: 'Laboratorio Goya Innova', city: '', address: '', phone: '' });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error(lang === 'es' ? 'Nombre obligatorio' : 'Name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/acumbamail/lists`, form, { withCredentials: true });
      toast.success(lang === 'es' ? 'Lista creada' : 'List created');
      setForm({ name: '', sender_email: '', company: 'Laboratorio Goya Innova', city: '', address: '', phone: '' });
      onCreated();
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="create-list-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{lb.createList}</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {lang === 'es' ? 'Crear una nueva lista de suscriptores en Acumbamail' : 'Create a new subscriber list in Acumbamail'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.listName} *</Label>
            <Input data-testid="new-list-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: ESTUDIO-2026-001" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.senderEmail}</Label>
            <Input data-testid="new-list-email" type="email" value={form.sender_email} onChange={e => setForm(p => ({ ...p, sender_email: e.target.value }))} placeholder="info@laboratoriogoya.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.company}</Label>
              <Input value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.city}</Label>
              <Input value={form.city} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} />
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="text-xs">{lb.cancel}</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="confirm-create-list">
            {saving ? lb.loading : lb.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CreateCampaignDialog({ open, onClose, onCreated, lists, templates, lang, lb, defaults }) {
  const [mode, setMode] = useState('template');
  const [form, setForm] = useState({
    name: '', subject: '', from_name: 'Laboratorio Goya Innova', from_email: '',
    list_id: '', template_id: '', body: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && defaults) {
      setForm(p => ({
        ...p,
        name: defaults.name || '',
        subject: defaults.subject || '',
        from_name: defaults.from_name || 'Laboratorio Goya Innova',
        from_email: defaults.from_email || '',
      }));
      setMode('template');
    } else if (!open) {
      setForm({ name: '', subject: '', from_name: 'Laboratorio Goya Innova', from_email: '', list_id: '', template_id: '', body: '' });
    }
  }, [open, defaults]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.list_id) {
      toast.error(lang === 'es' ? 'Nombre, asunto y lista son obligatorios' : 'Name, subject and list are required');
      return;
    }
    if (mode === 'template' && !form.template_id) {
      toast.error(lang === 'es' ? 'Selecciona una plantilla' : 'Select a template');
      return;
    }
    if (mode === 'html' && !form.body.trim()) {
      toast.error(lang === 'es' ? 'El contenido HTML es obligatorio' : 'HTML content is required');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'template') {
        await axios.post(`${API_URL}/api/acumbamail/campaigns/send-with-template`, {
          name: form.name, subject: form.subject,
          from_name: form.from_name, from_email: form.from_email,
          template_id: form.template_id, lists: [form.list_id],
        }, { withCredentials: true });
        toast.success(lang === 'es' ? 'Campana creada y enviada' : 'Campaign created and sent');
      } else {
        await axios.post(`${API_URL}/api/acumbamail/campaigns`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Campana creada (borrador)' : 'Campaign created (draft)');
      }
      setForm({ name: '', subject: '', from_name: 'Laboratorio Goya Innova', from_email: '', list_id: '', template_id: '', body: '' });
      onCreated();
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="create-campaign-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{lb.createCampaign}</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {lang === 'es' ? 'Configurar una nueva campana de email en Acumbamail' : 'Set up a new email campaign in Acumbamail'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Mode selector */}
          <div className="flex gap-1 p-0.5 bg-stone-100 rounded">
            <button type="button" onClick={() => setMode('template')} data-testid="campaign-mode-template"
              className={`flex-1 text-xs py-1.5 px-3 rounded transition-colors ${mode === 'template' ? 'bg-white shadow-sm font-semibold text-emerald-700' : 'text-stone-500 hover:text-stone-700'}`}>
              {lang === 'es' ? 'Con Plantilla' : 'With Template'}
            </button>
            <button type="button" onClick={() => setMode('html')} data-testid="campaign-mode-html"
              className={`flex-1 text-xs py-1.5 px-3 rounded transition-colors ${mode === 'html' ? 'bg-white shadow-sm font-semibold text-[#1f3e80]' : 'text-stone-500 hover:text-stone-700'}`}>
              {lang === 'es' ? 'HTML Manual' : 'Manual HTML'}
            </button>
          </div>
          {mode === 'template' && (
            <p className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
              {lang === 'es'
                ? 'Selecciona una plantilla de Acumbamail y la campana se creara y enviara automaticamente via webhook.'
                : 'Select an Acumbamail template and the campaign will be created and sent automatically via webhook.'}
            </p>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.name} *</Label>
            <Input data-testid="campaign-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: Reclutamiento Estudio ABC" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.subject} *</Label>
            <Input data-testid="campaign-subject" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="Ej: Nuevo estudio disponible" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.fromName}</Label>
              <Input data-testid="campaign-from-name" value={form.from_name} onChange={e => setForm(p => ({ ...p, from_name: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.fromEmail}</Label>
              <Input data-testid="campaign-from-email" type="email" value={form.from_email} onChange={e => setForm(p => ({ ...p, from_email: e.target.value }))} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.selectList} *</Label>
            <Select value={form.list_id} onValueChange={v => setForm(p => ({ ...p, list_id: v }))}>
              <SelectTrigger data-testid="campaign-list-select" className="text-xs">
                <SelectValue placeholder={lb.selectList} />
              </SelectTrigger>
              <SelectContent>
                {lists.map(l => (
                  <SelectItem key={l.id} value={l.id} className="text-xs">{l.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {mode === 'template' ? (
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.selectTemplate} *</Label>
              <Select value={form.template_id} onValueChange={v => setForm(p => ({ ...p, template_id: v }))}>
                <SelectTrigger data-testid="campaign-template-select" className="text-xs">
                  <SelectValue placeholder={lb.selectTemplate} />
                </SelectTrigger>
                <SelectContent className="max-h-48">
                  {templates.map(t => (
                    <SelectItem key={t.id} value={String(t.id)} className="text-xs" data-testid={`campaign-template-option-${t.id}`}>
                      {t.name}{t.available === false && <span className="ml-1.5 text-[10px] text-stone-400">({lb.unavailable})</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs">{lb.body} *</Label>
              <textarea
                data-testid="campaign-body"
                value={form.body}
                onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                className="w-full min-h-[150px] text-xs border border-stone-200 rounded p-2 font-mono focus:outline-none focus:ring-2 focus:ring-[#1f3e80]/30 focus:border-[#1f3e80]"
                placeholder={'<html>\n<body>\n  <h1>Hola |NOMBRE|</h1>\n  <p>Tu contenido aqui...</p>\n</body>\n</html>'}
              />
              <p className="text-[10px] text-stone-400">
                {lang === 'es' ? 'Usa |NOMBRE|, |EMAIL| como etiquetas de personalizacion.' : 'Use |NOMBRE|, |EMAIL| as personalization tags.'}
              </p>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="text-xs">{lb.cancel}</Button>
          <Button onClick={handleCreate} disabled={saving} data-testid="confirm-create-campaign"
            className={`text-white text-xs ${mode === 'template' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-[#1f3e80] hover:bg-[#2a4f8a]'}`}>
            {saving ? lb.loading : mode === 'template'
              ? (lang === 'es' ? 'Crear y Enviar' : 'Create & Send')
              : lb.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SyncVolunteersDialog({ open, onClose, listId, listName, onSynced, lang, lb }) {
  const [filter, setFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const handleSync = async () => {
    setSyncing(true);
    setResult(null);
    try {
      const r = await axios.post(`${API_URL}/api/acumbamail/lists/${listId}/sync-volunteers`, { filter }, { withCredentials: true });
      setResult(r.data);
      toast.success(`${r.data.synced} ${lb.synced}, ${r.data.skipped} ${lb.skipped}`);
    } catch (err) {
      toast.error(errText(err));
    }
    setSyncing(false);
  };

  return (
    <Dialog open={open} onOpenChange={() => { if (!syncing) { onClose(); setResult(null); } }}>
      <DialogContent className="sm:max-w-md" data-testid="sync-volunteers-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{lb.syncVolunteers}</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {lb.syncDesc}: <strong>{listName}</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.filter}</Label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="sync-filter" className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">{lb.allVolunteers}</SelectItem>
                <SelectItem value="active" className="text-xs">{lb.activeOnly}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-800">
              <strong>{result.synced}</strong> {lb.synced} &middot; <strong>{result.skipped}</strong> {lb.skipped}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { onClose(); setResult(null); }} disabled={syncing} className="text-xs">{lb.cancel}</Button>
          {result ? (
            <Button onClick={() => { onSynced(); setResult(null); }} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">OK</Button>
          ) : (
            <Button onClick={handleSync} disabled={syncing} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="confirm-sync">
              <Export size={14} className="mr-1.5" />
              {syncing ? lb.syncing : lb.syncVolunteers}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CreateTemplateDialog({ open, onClose, onCreated, lang, lb }) {
  const [form, setForm] = useState({ name: '', body: '' });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error(lang === 'es' ? 'Nombre obligatorio' : 'Name required'); return; }
    if (!form.body.trim()) { toast.error(lang === 'es' ? 'Contenido HTML obligatorio' : 'HTML content required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/acumbamail/templates`, form, { withCredentials: true });
      toast.success(lang === 'es' ? 'Plantilla creada' : 'Template created');
      setForm({ name: '', body: '' });
      onCreated();
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="create-template-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{lb.createTemplate}</DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {lang === 'es' ? 'Crear una nueva plantilla de email en Acumbamail' : 'Create a new email template in Acumbamail'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.templateName} *</Label>
            <Input data-testid="new-template-name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ej: Reclutamiento Estudio 2026" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">{lb.templateBody} *</Label>
            <textarea
              data-testid="new-template-body"
              value={form.body}
              onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
              className="w-full min-h-[200px] text-xs border border-stone-200 rounded p-2 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400"
              placeholder={'<html>\n<body>\n  <h1>Hola |NOMBRE|</h1>\n  <p>Tu contenido aqui...</p>\n</body>\n</html>'}
            />
            <p className="text-[10px] text-stone-400">
              {lang === 'es'
                ? 'Usa |NOMBRE|, |EMAIL| como etiquetas de personalizacion'
                : 'Use |NOMBRE|, |EMAIL| as personalization tags'}
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="text-xs">{lb.cancel}</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="confirm-create-template">
            {saving ? lb.loading : lb.save}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CampaignReportDialog({ open, onClose, data, loading, campaignName, lang, lb }) {
  if (!open) return null;

  const t = data?.totals || {};
  const basic = data?.basic || {};
  const openers = data?.openers || [];
  const isp = data?.isp || {};

  const openRate = t.emails_to_send > 0 ? ((t.opened / t.emails_to_send) * 100).toFixed(1) : '0';
  const clickRate = t.emails_to_send > 0 ? ((t.unique_clicks / t.emails_to_send) * 100).toFixed(1) : '0';
  const bounceRate = t.emails_to_send > 0 ? (((t.hard_bounces + t.soft_bounces) / t.emails_to_send) * 100).toFixed(1) : '0';

  const kpis = [
    { label: lb.sent, value: t.emails_to_send || 0, color: 'text-[#1f3e80]', bg: 'bg-blue-50 border-blue-200' },
    { label: lb.delivered, value: t.total_delivered || 0, color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
    { label: lb.opened, value: t.opened || 0, color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
    { label: lb.unopened, value: t.unopened || 0, color: 'text-stone-600', bg: 'bg-stone-50 border-stone-200' },
    { label: lb.uniqueClicks, value: t.unique_clicks || 0, color: 'text-cyan-700', bg: 'bg-cyan-50 border-cyan-200' },
    { label: lb.hardBounces, value: t.hard_bounces || 0, color: 'text-red-600', bg: 'bg-red-50 border-red-200' },
    { label: lb.softBounces, value: t.soft_bounces || 0, color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200' },
    { label: lb.unsubscribes, value: t.unsubscribes || 0, color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
    { label: lb.complaints, value: t.complaints || 0, color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  ];

  const rates = [
    { label: lb.openRate, value: `${openRate}%`, color: openRate > 50 ? 'text-green-700' : openRate > 20 ? 'text-amber-600' : 'text-red-600' },
    { label: lb.clickRate, value: `${clickRate}%`, color: clickRate > 5 ? 'text-green-700' : clickRate > 1 ? 'text-amber-600' : 'text-stone-500' },
    { label: lb.bounceRate, value: `${bounceRate}%`, color: bounceRate < 2 ? 'text-green-700' : bounceRate < 5 ? 'text-amber-600' : 'text-red-600' },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="campaign-report-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <ChartBar size={18} className="text-emerald-600" />
            {lb.reportTitle}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            {campaignName}
            {basic.date_sent && <span className="ml-2 text-stone-400"> — {basic.date_sent}</span>}
            {basic.subject && <span className="ml-2 text-stone-400"> — {basic.subject}</span>}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center">
            <p className="text-xs text-stone-400">{lb.loading}</p>
          </div>
        ) : !data ? (
          <p className="text-xs text-stone-400 text-center py-8">{lb.noReport}</p>
        ) : (
          <div className="space-y-5">
            {/* KPI Grid */}
            <div className="grid grid-cols-3 gap-2">
              {kpis.map(k => (
                <div key={k.label} className={`p-2.5 rounded border ${k.bg} text-center`}>
                  <p className={`text-lg font-bold font-mono ${k.color}`}>{k.value.toLocaleString()}</p>
                  <p className="text-[10px] text-stone-500 mt-0.5">{k.label}</p>
                </div>
              ))}
            </div>

            {/* Rates */}
            <div className="flex gap-3">
              {rates.map(r => (
                <div key={r.label} className="flex-1 p-3 rounded border border-stone-200 bg-white text-center">
                  <p className={`text-xl font-bold font-mono ${r.color}`}>{r.value}</p>
                  <p className="text-[10px] text-stone-500 mt-0.5">{r.label}</p>
                </div>
              ))}
            </div>

            {/* Campaign URL */}
            {t.campaign_url && (
              <a href={t.campaign_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-[#1f3e80] hover:underline" data-testid="campaign-url-link">
                <Eye size={14} /> {lb.campaignUrl}
              </a>
            )}

            {/* ISP Breakdown */}
            {Object.keys(isp).length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-stone-700 mb-2">{lb.ispBreakdown}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {Object.entries(isp).map(([name, stats]) => (
                    <div key={name} className="p-2 border border-stone-200 rounded text-center">
                      <p className="text-[11px] font-semibold text-stone-700 truncate">{name}</p>
                      <div className="flex justify-center gap-3 mt-1 text-[10px] text-stone-500">
                        <span className="text-green-600">{stats.opened || 0} ab.</span>
                        <span className="text-stone-400">{stats.unopened || 0} no</span>
                        <span className="text-red-500">{(stats.hard_bounces || 0) + (stats.soft_bounces || 0)} reb.</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Openers List */}
            {openers.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-stone-700 mb-2">
                  {lb.openersList} <Badge variant="outline" className="ml-1 text-[9px]">{openers.length}</Badge>
                </h3>
                <div className="max-h-[250px] overflow-y-auto border border-stone-200 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-stone-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2 font-medium text-stone-600">{lb.email}</th>
                        <th className="text-left p-2 font-medium text-stone-600">{lb.openDate}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openers.map((o, i) => (
                        <tr key={o.email + i} className="border-t border-stone-100 hover:bg-stone-50">
                          <td className="p-2 text-stone-700">{o.email}</td>
                          <td className="p-2 text-stone-500">{o.open_datetime || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="text-xs">{lb.close}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
